// src/styles/theme.ts
// Unified theme — single source of truth for all design tokens

export const fontFamily = 'Cairo, Arabic, sans-serif';

export const appTheme = {
  colors: {
    primary: {
      main: '#6523d1',
      light: 'rgba(101, 35, 209, 0.1)',
      dark: '#5019a8',
      gradient: 'linear-gradient(135deg, #6523d1 0%, #5019a8 100%)',
    },
    secondary: {
      main: 'rgb(251, 146, 60)',
      light: 'rgba(251, 146, 60, 0.1)',
      dark: 'rgb(234, 88, 12)',
    },
    background: {
      main: 'rgb(249, 250, 251)',
      paper: '#ffffff',
      hover: 'rgba(101, 35, 209, 0.04)',
    },
    text: {
      primary: 'rgb(17, 24, 39)',
      secondary: 'rgb(107, 114, 128)',
      tertiary: 'rgb(156, 163, 175)',
      disabled: 'rgb(209, 213, 219)',
    },
    border: {
      light: 'rgba(209, 213, 219, 0.3)',
      main: 'rgba(209, 213, 219, 0.5)',
      dark: 'rgba(107, 114, 128, 0.5)',
    },
    status: {
      success: 'rgb(34, 197, 94)',
      error: 'rgb(239, 68, 68)',
      warning: 'rgb(245, 158, 11)',
      info: 'rgb(59, 130, 246)',
    },

    // Order status colors
    orderStatus: {
      pending: 'rgb(245, 158, 11)',
      confirmed: 'rgb(59, 130, 246)',
      preparing: 'rgb(168, 85, 247)',
      ready: 'rgb(34, 197, 94)',
      outForDelivery: 'rgb(14, 165, 233)',
      completed: 'rgb(34, 197, 94)',
      cancelled: 'rgb(239, 68, 68)',
    },

    // Table management status
    tableStatus: {
      available: 'rgb(34, 197, 94)',
      occupied: 'rgb(239, 68, 68)',
      reserved: 'rgb(245, 158, 11)',
    },

    // Dish availability status
    dishStatus: {
      available: 'rgb(34, 197, 94)',
      unavailable: 'rgb(239, 68, 68)',
    },

    // Restaurant visibility status
    restaurantStatus: {
      public: {
        main: 'rgb(34, 197, 94)',
        light: 'rgba(34, 197, 94, 0.1)',
      },
      private: {
        main: 'rgb(239, 68, 68)',
        light: 'rgba(239, 68, 68, 0.1)',
      },
    },

    // Stat card colors (admin + restaurant dashboards)
    stats: {
      blue: {
        main: '#2196f3',
        light: '#2196f340',
        gradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      },
      green: {
        main: '#4caf50',
        light: '#4caf5040',
        gradient: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
      },
      orange: {
        main: '#ff9800',
        light: '#ff980040',
        gradient: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
      },
      purple: {
        main: '#6523d1',
        light: '#6523d140',
        gradient: 'linear-gradient(135deg, #6523d1 0%, #5019a8 100%)',
      },

      // Restaurant dashboard specific stat colors
      categories: {
        main: 'rgb(59, 130, 246)',
        light: 'rgba(59, 130, 246, 0.1)',
        bg: 'rgba(59, 130, 246, 0.05)',
      },
      dishes: {
        main: 'rgb(251, 146, 60)',
        light: 'rgba(251, 146, 60, 0.1)',
        bg: 'rgba(251, 146, 60, 0.05)',
      },
      available: {
        main: 'rgb(34, 197, 94)',
        light: 'rgba(34, 197, 94, 0.1)',
        bg: 'rgba(34, 197, 94, 0.05)',
      },
    },

    // Info icon colors (restaurant profile)
    info: {
      phone: {
        main: 'rgb(34, 197, 94)',
        light: 'rgba(34, 197, 94, 0.1)',
      },
      location: {
        main: 'rgb(59, 130, 246)',
        light: 'rgba(59, 130, 246, 0.1)',
      },
      schedule: {
        main: 'rgb(251, 146, 60)',
        light: 'rgba(251, 146, 60, 0.1)',
      },
      visibility: {
        main: '#6523d1',
        light: 'rgba(101, 35, 209, 0.1)',
      },
    },

    // Data table colors
    table: {
      headerBg: 'transparent',
      headerText: '#9CA3AF',
      headerBorder: 'transparent',
      bodyText: '#1E293B',
      bodyTextSecondary: '#6B7280',
      bodyBorder: '#F3F4F6',
      hoverBg: '#FAFAFA',
      dragHandle: '#D1D5DB',
      dragHandleHover: '#8B5CF6',
      dragHandleDisabled: '#E5E7EB',
      editIcon: '#3B82F6',
      deleteIcon: '#EF4444',
    },
  },

  gradients: {
    primary: 'linear-gradient(135deg, #6523d1 0%, #5019a8 100%)',
    secondary: 'linear-gradient(135deg, #6523d1 0%, rgb(251, 146, 60) 100%)',
  },

  typography: {
    fontFamily,
    sizes: {
      xs: '0.75rem',
      sm: '0.813rem',
      md: '0.875rem',
      lg: '1rem',
      xl: '1.25rem',
      xxl: '1.5rem',
    },
  },

  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    none: 'none',
    card: '0 4px 12px rgba(0, 0, 0, 0.08)',
  },

  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '500ms cubic-bezier(0.4, 0, 0.2, 1)',
  },

  borderRadius: {
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    full: '9999px',
  },

  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
  },
};

export const getThemeColors = (mode: 'light' | 'dark') => {
  const isDark = mode === 'dark';
  return {
    text: {
      primary: isDark ? '#F9FAFB' : '#111827',
      secondary: isDark ? '#9CA3AF' : '#6B7280',
    }
  };
};
